'use client';

import LearningPathCard from '@/components/common/learning-path-card';
import { getCoursesWithQuizCount, getVideosCountFromCourses } from '@/lib/utils/learning-path-counts';
import {
  computeLpAssessmentUnlocked,
  learningPathShowsCertificatePromo,
} from '@/lib/utils/learning-path-utils';
import type { UserLearningPath } from '@/types/learning-paths';
import { useEnrolledLearningPaths, type UserLearningPathData } from '@/hooks/useUserLearningPaths';

interface LearningPath {
  uid: string;
  url: string;
  is_assessment?: boolean;
  learning_pathway_details?: {
    thumbnail?: { url: string };
    headline?: string;
    description?: string;
    awards_certifications?: string | boolean;
    videos_count?: number;
  };
  courses?: {
    course?: Array<{
      chapters?: Array<{ lessons?: { lessons?: Array<{ video_lesson?: unknown[]; is_quiz?: boolean; quiz?: { question_count?: number; assessment?: unknown[] } }> } }>;
    }>;
  };
}

interface ProductLearningPathsSectionProps {
  learningPaths: LearningPath[] | LearningPath | { entries?: LearningPath[] };
}

export default function ProductLearningPathsSection({
  learningPaths,
}: ProductLearningPathsSectionProps) {
  const { userLearningPaths, isLoggedIn } = useEnrolledLearningPaths();

  const paths = (() => {
    if (Array.isArray(learningPaths)) {
      return learningPaths;
    }
    if (learningPaths && typeof learningPaths === 'object' && 'entries' in learningPaths && Array.isArray((learningPaths as { entries?: LearningPath[] }).entries)) {
      const entries = (learningPaths as { entries: LearningPath[] }).entries;
      return entries.length > 0 ? entries : [];
    }
    if (learningPaths && typeof learningPaths === 'object' && 'uid' in learningPaths) {
      return [learningPaths as LearningPath];
    }
    return [];
  })();

  return (
    <div>
      {paths.map((path: LearningPath) => {
        // Gate the lookup itself on isLoggedIn, not just isEnrolled below — enabled
        // turning off on logout doesn't clear this query's already-fetched data, so
        // progressPercent/isCompleted/hasStarted would otherwise still leak the
        // previous session's values into the card even though isEnrolled is false.
        const userPath = isLoggedIn
          ? userLearningPaths?.find(
              (up: UserLearningPathData) =>
                (up.entryUid || up.learningPathUid) === path.uid && up.isSubscribed,
            )
          : undefined;
        const isEnrolled = isLoggedIn && !!userPath;
        const progressPercent = userPath?.progress?.percentage;
        const isCompleted = userPath?.progress?.isCompleted;
        const lpAssessmentUnlocked = userPath
          ? computeLpAssessmentUnlocked(userPath as UserLearningPath)
          : false;

        const cmsVideosCount = path.learning_pathway_details?.videos_count;
        const videosCount =
          cmsVideosCount != null && cmsVideosCount > 0
            ? cmsVideosCount
            : getVideosCountFromCourses(path.courses ?? {});

        return (
          <LearningPathCard
            key={path.uid}
            uid={path.uid}
            url={`/learning-paths/${path.url}`}
            thumbnail={path.learning_pathway_details?.thumbnail?.url}
            title={path.learning_pathway_details?.headline || ''}
            description={path.learning_pathway_details?.description || ''}
            hasCertificate={learningPathShowsCertificatePromo(
              path.learning_pathway_details?.awards_certifications
            )}
            coursesCount={path.courses?.course?.length || 0}
            videosCount={videosCount > 0 ? videosCount : undefined}
            quizzesCount={getCoursesWithQuizCount(path.courses ?? {})}
            assessmentCount={path.is_assessment ? 1 : undefined}
            isEnrolled={isEnrolled}
            hasStarted={userPath?.progress?.hasStarted}
            progressPercent={progressPercent}
            isCompleted={isCompleted}
            isLearningMaterialCompleted={lpAssessmentUnlocked}
            hasAssessment={path.is_assessment === true}
          />
        );
      })}
    </div>
  );
}
